import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  HostListener,
  OnInit,
  QueryList,
  ViewChildren,
  inject
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { InputComponent } from 'src/app/components/shared/input';
import { DropdownComponent } from 'src/app/components/shared/dropdown';
import { TrashComponent } from 'src/app/components/icons/trash';
import { PlusComponent } from 'src/app/components/icons/plus';
import { CountryPickerComponent } from 'src/app/components/shared/country-picker';
import { LocaleService } from 'src/app/services/locale';
import { CategoryService } from 'src/app/services/category';

@Component({
  selector: 'app-category5-question3',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputComponent,
    DropdownComponent,
    TrashComponent,
    PlusComponent,
    CountryPickerComponent
  ],
  template: `
    <div class="text-[12px] text-[#616161] mt-1">
      {{ strings[language].answer }}
    </div>
    <!-- Usage toggle -->
    <div class="flex flex-wrap gap-4 sm:gap-6 mt-4">
      <div
        (click)="toggleUsage(true)"
        class="cursor-pointer flex gap-2 items-center"
      >
        <div
          [class.bg-green-950]="usage"
          class="w-4 h-4 border rounded-full flex-shrink-0"
        ></div>
        <span>{{ strings[language].choice_yes }}</span>
      </div>
      <div
        (click)="toggleUsage(false)"
        class="cursor-pointer flex gap-2 items-center"
      >
        <div
          [class.bg-green-950]="!usage"
          class="w-4 h-4 border rounded-full flex-shrink-0"
        ></div>
        <span>{{ strings[language].choice_no }}</span>
      </div>
    </div>
    <!-- Add Product Group -->
    <div class="flex justify-end mb-2 items-center" *ngIf="usage">
      <div
        (click)="addProductGroup()"
        class="bg-[#F8F9FA] px-3 py-2 rounded text-white text-md cursor-pointer"
      >
        <app-plus></app-plus>
      </div>
    </div>
    <form *ngIf="usage" [formGroup]="form" class="space-y-10 mt-4">
      <div formArrayName="productGroups" class="space-y-6">
        <div
          *ngFor="let group of productGroups.controls; let i = index"
          [formGroupName]="i"
          class="border p-4 rounded-lg space-y-4"
        >
          <div class="flex justify-between items-center">
            <h2 class="text-lg font-semibold">
              {{ strings[language].main_label }} {{ i + 1 }}
            </h2>
            <div
              class="cursor-pointer"
              *ngIf="productGroups.length > 1"
              (click)="removeProductGroup(i)"
            >
              <app-trash></app-trash>
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <!-- Product Label -->
            <div>
              <label class="block text-sm font-medium mb-1">{{
                strings[language].label_1
              }}</label>
              <app-input
                [placeholder]="strings[language].placeholder_product"
                [control]="getFormControl(group, 'product_label')"
              ></app-input>
            </div>

            <!-- Mass Sold -->
            <div>
              <label class="block text-sm font-medium mb-1">{{
                strings[language].label_2
              }}</label>
              <app-input
                type="number"
                [placeholder]="strings[language].placeholder_mass"
                [control]="getFormControl(group, 'mass_sold')"
              ></app-input>
            </div>

            <!-- Mass Unit -->
            <div>
              <label class="block text-sm font-medium mb-1">{{
                strings[language].label_3
              }}</label>
              <app-dropdown
                [placeholder]="strings[language].placeholder_unit"
                [options]="unitOptions"
                [control]="getFormControl(group, 'mass_unit')"
              ></app-dropdown>
            </div>

            <!-- Reporting Year -->
            <div>
              <label class="block text-sm font-medium mb-1">{{
                strings[language].label_4
              }}</label>
              <app-dropdown
                [placeholder]="strings[language].placeholder_year"
                [options]="yearOptions"
                [control]="getFormControl(group, 'rep_year')"
              ></app-dropdown>
            </div>

            <!-- Region/Market -->
            <div>
              <label class="block text-sm font-medium mb-1">{{
                strings[language].label_5
              }}</label>
              <app-country-picker
                [control]="getFormControl(group, 'region')"
              ></app-country-picker>
            </div>

            <!-- Notes -->
            <div class="col-span-2">
              <label class="block text-sm font-medium mb-1">{{
                strings[language].label_6
              }}</label>
              <textarea
                class="w-full border border-[#CED4DA] rounded p-2"
                rows="3"
                [placeholder]="strings[language].placeholder_notes"
                [formControl]="getFormControl(group, 'note')"
              ></textarea>
            </div>
          </div>

          <!-- EOL Rows Section -->
          <div formArrayName="eol" class="border rounded p-4">
            <div class="flex justify-between items-center mb-4">
              <h3 class="text-lg font-medium">
                {{ strings[language].main_label_1 }}
              </h3>
              <button
                type="button"
                class="bg-[#F8F9FA] px-3 py-2 rounded text-white"
                (click)="addEolRow(getFormArray(group, 'eol'))"
                [attr.aria-label]="strings[language].add_eol_scenario"
              >
                <app-plus></app-plus>
              </button>
            </div>

            <div
              *ngFor="
                let row of getFormArray(group, 'eol').controls;
                let j = index
              "
              [formGroupName]="j"
              class="grid grid-cols-12 gap-4 items-end mb-2"
            >
              <!-- Treatment Method -->
              <div class="col-span-4">
                <label class="block text-sm font-medium mb-1">{{
                  strings[language].label_7
                }}</label>
                <app-dropdown
                  [placeholder]="strings[language].placeholder_treatment"
                  [options]="getTreatmentMethod()"
                  [control]="getFormControl(row, 'eol_method')"
                ></app-dropdown>
              </div>

              <!-- Material Type -->
              <div class="col-span-4">
                <label class="block text-sm font-medium mb-1">{{
                  strings[language].label_8
                }}</label>
                <app-dropdown
                  [placeholder]="strings[language].placeholder_material"
                  [options]="
                    getSubcategories(getFormControl(row, 'eol_method').value)
                  "
                  [control]="getFormControl(row, 'waste_type')"
                ></app-dropdown>
              </div>

              <!-- Share Percentage -->
              <div class="col-span-3">
                <label class="block text-sm font-medium mb-1">{{
                  strings[language].label_9
                }}</label>
                <app-input
                  type="number"
                  [placeholder]="strings[language].placeholder_share"
                  [control]="getFormControl(row, 'share_pct')"
                ></app-input>
              </div>

              <!-- Remove Button -->
              <div
                class="col-span-1 flex justify-center items-center cursor-pointer mb-3"
                (click)="removeEolRow(getFormArray(group, 'eol'), j)"
                [attr.aria-label]="'Remove scenario ' + (j + 1)"
              >
                <app-trash></app-trash>
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  `
})
export class Category5Question3Component implements OnInit {
  @ViewChildren('closeDropdown') dropdownContainers!: QueryList<ElementRef>;
  language = inject(LocaleService).getLanguage();
  categoryService = inject(CategoryService);
  form!: FormGroup;
  usage = false;

  strings: any = {
    english: {
      // Main labels
      answer: 'Please fill in the required data if yes to proceed.',
      choice_yes: 'Yes',
      choice_no: 'No',
      main_label: 'Product Group',
      main_label_1: 'End-of-life scenario',
      add_product: 'Add product',
      add_eol_scenario: 'Add end-of-life scenario',

      // Product group labels
      label_1: 'Product label',
      label_2: 'Mass sold in reporting year',
      label_3: 'Mass unit',
      label_4: 'Reporting year',
      label_5: 'Region / market',
      label_6: 'Notes',

      // EOL scenario labels
      label_7: 'Treatment method',
      label_8: 'Material type',
      label_9: 'Share (%)',

      // Placeholders
      placeholder_product: 'Enter product name',
      placeholder_mass: 'Enter mass sold',
      placeholder_unit: 'Select mass unit',
      placeholder_year: 'Select reporting year',
      placeholder_region: 'Select region/market',
      placeholder_notes: 'Enter any additional notes',
      placeholder_treatment: 'Select treatment method',
      placeholder_material: 'Select material type',
      placeholder_share: 'Enter percentage share'
    },
    italian: {
      // Main labels
      answer:
        'Si prega di inserire i dati richiesti se la risposta è sì per procedere.',
      choice_yes: 'Sì',
      choice_no: 'No',
      main_label: 'prodotto',
      main_label_1: 'Scenario di fine vita',
      add_product: 'Aggiungi prodotto',
      add_eol_scenario: 'Aggiungi scenario di fine vita',

      // Product group labels
      label_1: 'prodotto',
      label_2: 'Massa totale venduta nell’anno di rendicontazione',
      label_3: 'Unità di misura della massa',
      label_4: 'Anno di rendicontazione',
      label_5: 'Regione / mercato',
      label_6: 'Note',

      // EOL scenario labels
      label_7: 'Metodo di trattamento',
      label_8: 'Tipo di materiale',
      label_9: 'Quota (%)',

      // Placeholders
      placeholder_product: 'Inserisci il nome del prodotto',
      placeholder_mass: 'Inserire la massa totale venduta',
      placeholder_unit: 'Seleziona unità di massa',
      placeholder_year: 'Seleziona anno di rendicontazione',
      placeholder_region: 'Seleziona regione/mercato',
      placeholder_notes: 'Inserisci eventuali note aggiuntive',
      placeholder_treatment: 'Seleziona metodo di trattamento',
      placeholder_material: 'Seleziona tipo di materiale',
      placeholder_share: 'Inserisci la percentuale'
    }
  };
  WASTE_TREATMENT_METHODS: any[] = [
    {
      name: 'Landfill',
      itaName: 'Smaltimento in discarica',
      subcategories: [
        {
          id: 'waste-type_organic_food_and_drink-disposal_method_landfill',
          label: 'Organic food & drink waste via landfill',
          itaLabel: 'Rifiuti organici alimentari e bevande in discarica'
        },
        {
          id: 'waste-type_organic_garden-disposal_method_landfill',
          label: 'Organic garden waste via landfill',
          itaLabel: 'Rifiuti organici da giardinaggio in discarica'
        },
        {
          id: 'waste-type_household_residual_waste-disposal_method_landfill',
          label: 'Household residual waste via landfill',
          itaLabel: 'Rifiuti residui domestici in discarica'
        },
        {
          id: 'waste-type_commercial_and_industrial-disposal_method_landfill',
          label: 'Commercial & industrial waste via landfill',
          itaLabel: 'Rifiuti commerciali e industriali in discarica'
        },
        {
          id: 'waste-type_paper_and_cardboard-disposal_method_landfill',
          label: 'Paper & cardboard waste via landfill',
          itaLabel: 'Rifiuti di carta e cartone in discarica'
        },
        {
          id: 'waste-type_plastics-disposal_method_landfill',
          label: 'Plastic waste via landfill',
          itaLabel: 'Rifiuti plastici in discarica'
        },
        {
          id: 'waste-type_glass-disposal_method_landfill',
          label: 'Glass waste via landfill',
          itaLabel: 'Rifiuti di vetro in discarica'
        },
        {
          id: 'waste-type_metals-disposal_method_landfill',
          label: 'Metal waste via landfill',
          itaLabel: 'Rifiuti metallici in discarica'
        },
        {
          id: 'waste-type_wood-disposal_method_landfill',
          label: 'Wood waste via landfill',
          itaLabel: 'Rifiuti di legno in discarica'
        },
        {
          id: 'waste-type_clothing-disposal_method_landfill',
          label: 'Clothing waste via landfill',
          itaLabel: 'Rifiuti di abbigliamento in discarica'
        },
        {
          id: 'waste-type_aggregates-disposal_method_landfill',
          label: 'Aggregate waste via landfill',
          itaLabel: 'Rifiuti di aggregati in discarica'
        },
        {
          id: 'waste-type_asphalt-disposal_method_landfill',
          label: 'Asphalt waste via landfill',
          itaLabel: 'Rifiuti di asfalto in discarica'
        },
        {
          id: 'waste-type_bricks-disposal_method_landfill',
          label: 'Brick waste via landfill',
          itaLabel: 'Rifiuti di mattoni in discarica'
        },
        {
          id: 'waste-type_concrete-disposal_method_landfill',
          label: 'Concrete waste via landfill',
          itaLabel: 'Rifiuti di calcestruzzo in discarica'
        },
        {
          id: 'waste-type_plasterboard-disposal_method_landfill',
          label: 'Plasterboard waste via landfill',
          itaLabel: 'Rifiuti di cartongesso in discarica'
        },
        {
          id: 'waste-type_soils-disposal_method_landfill',
          label: 'Soil waste via landfill',
          itaLabel: 'Rifiuti di terreno in discarica'
        },
        {
          id: 'waste-type_insulation-disposal_method_landfill',
          label: 'Insulation waste via landfill',
          itaLabel: 'Rifiuti di materiali isolanti in discarica'
        },
        {
          id: 'waste-type_asbestos-disposal_method_landfill',
          label: 'Asbestos waste via landfill',
          itaLabel: 'Rifiuti di amianto in discarica'
        },
        {
          id: 'waste-type_batteries-disposal_method_landfill',
          label: 'Battery waste via landfill',
          itaLabel: 'Rifiuti di batterie in discarica'
        },
        {
          id: 'waste-type_weee_fridges_and_freezers-disposal_method_landfill',
          label: 'WEEE fridges & freezers via landfill',
          itaLabel: 'RAEE frigoriferi e congelatori in discarica'
        },
        {
          id: 'waste-type_weee_large-disposal_method_landfill',
          label: 'WEEE large appliances via landfill',
          itaLabel: 'RAEE grandi elettrodomestici in discarica'
        },
        {
          id: 'waste-type_weee_mixed-disposal_method_landfill',
          label: 'WEEE mixed appliances via landfill',
          itaLabel: 'RAEE elettrodomestici misti in discarica'
        },
        {
          id: 'waste-type_weee_small-disposal_method_landfill',
          label: 'WEEE small appliances via landfill',
          itaLabel: 'RAEE piccoli elettrodomestici in discarica'
        }
      ]
    },
    {
      name: 'Combustion / Incineration (no energy recovery)',
      itaName: 'Combustione / Incenerimento (senza recupero energetico)',
      subcategories: [
        {
          id: 'waste-type_organic_food_and_drink-disposal_method_combustion',
          label: 'Organic food & drink waste via combustion',
          itaLabel: 'Rifiuti organici alimentari e bevande tramite combustione'
        },
        {
          id: 'waste-type_organic_garden-disposal_method_combustion',
          label: 'Organic garden waste via combustion',
          itaLabel: 'Rifiuti organici da giardinaggio tramite combustione'
        },
        {
          id: 'waste-type_household_residual_waste-disposal_method_combustion',
          label: 'Household residual waste via combustion',
          itaLabel: 'Rifiuti residui domestici tramite combustione'
        },
        {
          id: 'waste-type_commercial_and_industrial-disposal_method_combustion',
          label: 'Commercial & industrial waste via combustion',
          itaLabel: 'Rifiuti commerciali e industriali tramite combustione'
        },
        {
          id: 'waste-type_paper_and_cardboard-disposal_method_combustion',
          label: 'Paper & cardboard waste via combustion',
          itaLabel: 'Rifiuti di carta e cartone tramite combustione'
        },
        {
          id: 'waste-type_plastics-disposal_method_combustion',
          label: 'Plastic waste via combustion',
          itaLabel: 'Rifiuti plastici tramite combustione'
        },
        {
          id: 'waste-type_glass-disposal_method_combustion',
          label: 'Glass waste via combustion',
          itaLabel: 'Rifiuti di vetro tramite combustione'
        },
        {
          id: 'waste-type_metals-disposal_method_combustion',
          label: 'Metal waste via combustion',
          itaLabel: 'Rifiuti metallici tramite combustione'
        },
        {
          id: 'waste-type_wood-disposal_method_combustion',
          label: 'Wood waste via combustion',
          itaLabel: 'Rifiuti di legno tramite combustione'
        },
        {
          id: 'waste-type_clothing-disposal_method_combustion',
          label: 'Clothing waste via combustion',
          itaLabel: 'Rifiuti di abbigliamento tramite combustione'
        },
        {
          id: 'waste-type_construction-disposal_method_combustion',
          label: 'Construction waste via combustion',
          itaLabel: 'Rifiuti edili tramite combustione'
        },
        {
          id: 'waste-type_mineral_oil-disposal_method_combustion',
          label: 'Mineral-oil waste via combustion',
          itaLabel: 'Rifiuti di olio minerale tramite combustione'
        },
        {
          id: 'waste-type_weee_large-disposal_method_combustion',
          label: 'WEEE large appliances via combustion',
          itaLabel: 'RAEE grandi elettrodomestici tramite combustione'
        },
        {
          id: 'waste-type_weee_mixed-disposal_method_combustion',
          label: 'WEEE mixed appliances via combustion',
          itaLabel: 'RAEE elettrodomestici misti tramite combustione'
        },
        {
          id: 'waste-type_weee_small-disposal_method_combustion',
          label: 'WEEE small appliances via combustion',
          itaLabel: 'RAEE piccoli elettrodomestici tramite combustione'
        }
      ]
    },
    {
      name: 'Composting',
      itaName: 'Compostaggio',
      subcategories: [
        {
          id: 'waste-type_organic_food_and_drink-disposal_method_composting',
          label: 'Organic food & drink waste via composting',
          itaLabel: 'Rifiuti organici alimentari e bevande tramite compostaggio'
        },
        {
          id: 'waste-type_organic_garden-disposal_method_composting',
          label: 'Organic garden waste via composting',
          itaLabel: 'Rifiuti organici da giardinaggio tramite compostaggio'
        },
        {
          id: 'waste-type_household_residual_waste-disposal_method_composting',
          label: 'Household residual waste via composting',
          itaLabel: 'Rifiuti residui domestici tramite compostaggio'
        },
        {
          id: 'waste-type_commercial_and_industrial-disposal_method_composting',
          label: 'Commercial & industrial waste via composting',
          itaLabel: 'Rifiuti commerciali e industriali tramite compostaggio'
        },
        {
          id: 'waste-type_paper_and_cardboard-disposal_method_composting',
          label: 'Paper & cardboard waste via composting',
          itaLabel: 'Rifiuti di carta e cartone tramite compostaggio'
        },
        {
          id: 'waste-type_wood-disposal_method_composting',
          label: 'Wood waste via composting',
          itaLabel: 'Rifiuti di legno tramite compostaggio'
        }
      ]
    },
    {
      name: 'Anaerobic digestion',
      itaName: 'Digestione anaerobica',
      subcategories: [
        {
          id:
            'waste-type_organic_food_and_drink-disposal_method_anaerobic_digestion',
          label: 'Organic food & drink waste via AD',
          itaLabel:
            'Rifiuti organici alimentari e bevande tramite digestione anaerobica'
        },
        {
          id: 'waste-type_organic_garden-disposal_method_anaerobic_digestion',
          label: 'Organic garden waste via AD',
          itaLabel:
            'Rifiuti organici da giardinaggio tramite digestione anaerobica'
        },
        {
          id:
            'waste-type_household_residual_waste-disposal_method_anaerobic_digestion',
          label: 'Household residual waste via AD',
          itaLabel: 'Rifiuti residui domestici tramite digestione anaerobica'
        },
        {
          id:
            'waste-type_commercial_and_industrial-disposal_method_anaerobic_digestion',
          label: 'Commercial & industrial waste via AD',
          itaLabel:
            'Rifiuti commerciali e industriali tramite digestione anaerobica'
        }
      ]
    },
    {
      name: 'Closed-loop recycling',
      itaName: 'Riciclaggio a circuito chiuso',
      subcategories: [
        {
          id:
            'waste-type_commercial_and_industrial-disposal_method_closed_loop',
          label: 'Commercial & industrial waste via closed-loop recycling',
          itaLabel:
            'Rifiuti commerciali e industriali tramite riciclaggio a circuito chiuso'
        },
        {
          id: 'waste-type_paper_and_cardboard-disposal_method_closed_loop',
          label: 'Paper & cardboard waste via closed-loop recycling',
          itaLabel:
            'Rifiuti di carta e cartone tramite riciclaggio a circuito chiuso'
        },
        {
          id: 'waste-type_plastics-disposal_method_closed_loop',
          label: 'Plastic waste via closed-loop recycling',
          itaLabel: 'Rifiuti plastici tramite riciclaggio a circuito chiuso'
        },
        {
          id: 'waste-type_glass-disposal_method_closed_loop',
          label: 'Glass waste via closed-loop recycling',
          itaLabel: 'Rifiuti di vetro tramite riciclaggio a circuito chiuso'
        },
        {
          id: 'waste-type_metals-disposal_method_closed_loop',
          label: 'Metal waste via closed-loop recycling',
          itaLabel: 'Rifiuti metallici tramite riciclaggio a circuito chiuso'
        },
        {
          id: 'waste-type_wood-disposal_method_closed_loop',
          label: 'Wood waste via closed-loop recycling',
          itaLabel: 'Rifiuti di legno tramite riciclaggio a circuito chiuso'
        },
        {
          id: 'waste-type_clothing-disposal_method_closed_loop',
          label: 'Clothing waste via closed-loop recycling',
          itaLabel:
            'Rifiuti di abbigliamento tramite riciclaggio a circuito chiuso'
        },
        {
          id: 'waste-type_aggregates-disposal_method_closed_loop',
          label: 'Aggregate waste via closed-loop recycling',
          itaLabel: 'Rifiuti di aggregati tramite riciclaggio a circuito chiuso'
        },
        {
          id: 'waste-type_asphalt-disposal_method_closed_loop',
          label: 'Asphalt waste via closed-loop recycling',
          itaLabel: 'Rifiuti di asfalto tramite riciclaggio a circuito chiuso'
        },
        {
          id: 'waste-type_concrete-disposal_method_closed_loop',
          label: 'Concrete waste via closed-loop recycling',
          itaLabel:
            'Rifiuti di calcestruzzo tramite riciclaggio a circuito chiuso'
        },
        {
          id: 'waste-type_construction-disposal_method_closed_loop',
          label: 'Construction waste via closed-loop recycling',
          itaLabel: 'Rifiuti edili tramite riciclaggio a circuito chiuso'
        },
        {
          id: 'waste-type_plasterboard-disposal_method_closed_loop',
          label: 'Plasterboard waste via closed-loop recycling',
          itaLabel:
            'Rifiuti di cartongesso tramite riciclaggio a circuito chiuso'
        },
        {
          id: 'waste-type_soils-disposal_method_closed_loop',
          label: 'Soil waste via closed-loop recycling',
          itaLabel: 'Rifiuti di terreno tramite riciclaggio a circuito chiuso'
        },
        {
          id: 'waste-type_insulation-disposal_method_closed_loop',
          label: 'Insulation waste via closed-loop recycling',
          itaLabel:
            'Rifiuti di materiali isolanti tramite riciclaggio a circuito chiuso'
        },
        {
          id: 'waste-type_mineral_oil-disposal_method_closed_loop',
          label: 'Mineral-oil waste via closed-loop recycling',
          itaLabel:
            'Rifiuti di olio minerale tramite riciclaggio a circuito chiuso'
        },
        {
          id: 'waste-type_tires-disposal_method_closed_loop',
          label: 'Tire waste via closed-loop recycling',
          itaLabel:
            'Rifiuti di pneumatici tramite riciclaggio a circuito chiuso'
        }
      ]
    },
    {
      name: 'Open-loop recycling',
      itaName: 'Riciclaggio a circuito aperto',
      subcategories: [
        {
          id: 'waste-type_paper_and_cardboard-disposal_method_open_loop',
          label: 'Paper & cardboard waste via open-loop recycling',
          itaLabel:
            'Rifiuti di carta e cartone tramite riciclaggio a circuito aperto'
        },
        {
          id: 'waste-type_plastics-disposal_method_open_loop',
          label: 'Plastic waste via open-loop recycling',
          itaLabel: 'Rifiuti plastici tramite riciclaggio a circuito aperto'
        },
        {
          id: 'waste-type_glass-disposal_method_open_loop',
          label: 'Glass waste via open-loop recycling',
          itaLabel: 'Rifiuti di vetro tramite riciclaggio a circuito aperto'
        },
        {
          id: 'waste-type_metals-disposal_method_open_loop',
          label: 'Metal waste via open-loop recycling',
          itaLabel: 'Rifiuti metallici tramite riciclaggio a circuito aperto'
        },
        {
          id: 'waste-type_wood-disposal_method_open_loop',
          label: 'Wood waste via open-loop recycling',
          itaLabel: 'Rifiuti di legno tramite riciclaggio a circuito aperto'
        },
        {
          id: 'waste-type_aggregates-disposal_method_open_loop',
          label: 'Aggregate waste via open-loop recycling',
          itaLabel: 'Rifiuti di aggregati tramite riciclaggio a circuito aperto'
        },
        {
          id: 'waste-type_asphalt-disposal_method_open_loop',
          label: 'Asphalt waste via open-loop recycling',
          itaLabel: 'Rifiuti di asfalto tramite riciclaggio a circuito aperto'
        },
        {
          id: 'waste-type_bricks-disposal_method_open_loop',
          label: 'Brick waste via open-loop recycling',
          itaLabel: 'Rifiuti di mattoni tramite riciclaggio a circuito aperto'
        },
        {
          id: 'waste-type_concrete-disposal_method_open_loop',
          label: 'Concrete waste via open-loop recycling',
          itaLabel:
            'Rifiuti di calcestruzzo tramite riciclaggio a circuito aperto'
        },
        {
          id: 'waste-type_construction-disposal_method_open_loop',
          label: 'Construction waste via open-loop recycling',
          itaLabel: 'Rifiuti edili tramite riciclaggio a circuito aperto'
        },
        {
          id: 'waste-type_batteries-disposal_method_open_loop',
          label: 'Battery waste via open-loop recycling',
          itaLabel: 'Rifiuti di batterie tramite riciclaggio a circuito aperto'
        },
        {
          id: 'waste-type_weee_fridges_and_freezers-disposal_method_open_loop',
          label: 'WEEE fridges & freezers via open-loop recycling',
          itaLabel:
            'RAEE frigoriferi e congelatori tramite riciclaggio a circuito aperto'
        },
        {
          id: 'waste-type_weee_large-disposal_method_open_loop',
          label: 'WEEE large appliances via open-loop recycling',
          itaLabel:
            'RAEE grandi elettrodomestici tramite riciclaggio a circuito aperto'
        },
        {
          id: 'waste-type_weee_mixed-disposal_method_open_loop',
          label: 'WEEE mixed appliances via open-loop recycling',
          itaLabel:
            'RAEE elettrodomestici misti tramite riciclaggio a circuito aperto'
        },
        {
          id: 'waste-type_weee_small-disposal_method_open_loop',
          label: 'WEEE small appliances via open-loop recycling',
          itaLabel:
            'RAEE piccoli elettrodomestici tramite riciclaggio a circuito aperto'
        }
      ]
    }
  ];
  unitOptions = [
    { value: 'kg', label: 'kg' },
    { value: 't', label: 't' }
  ];

  treatmentOptions = [
    { value: 'landfill', label: 'Landfill' },
    { value: 'incineration', label: 'Incineration' },
    { value: 'recycling_mechanical', label: 'Recycling (mechanical)' },
    { value: 'recycling_chemical', label: 'Recycling (chemical)' },
    { value: 'composting', label: 'Composting' },
    { value: 'digestion', label: 'Anaerobic digestion' },
    { value: 'take_back', label: 'Take-back scheme' }
  ];

  materialOptions = [
    { value: 'plastic', label: 'Plastic' },
    { value: 'metal', label: 'Metal' },
    { value: 'glass', label: 'Glass' },
    { value: 'organic', label: 'Organic' },
    { value: 'paper', label: 'Paper' },
    { value: 'mixed', label: 'Mixed' }
  ];

  currentYear = new Date().getFullYear();
  startYear = 2019;
  yearOptions = Array.from(
    { length: this.currentYear - this.startYear + 1 },
    (_, i) => {
      const year = this.startYear + i;
      return { label: String(year), value: year };
    }
  );

  constructor(private fb: FormBuilder) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      productGroups: this.fb.array([])
    });

    const categoriesRaw = localStorage.getItem('categories');
    let categories = [];

    if (categoriesRaw) {
      try {
        categories = JSON.parse(categoriesRaw);
        while (categories.length < 5)
          categories.push(this.createDefaultCategory());

        const answers = categories[4]?.questions?.[2]?.answers;
        if (answers && Array.isArray(answers)) {
          this.usage = true;
          answers.forEach((ans: any) => {
            const group = this.fb.group({
              product_label: [ans.product_label || '', Validators.required],
              mass_sold: [
                ans.mass_sold || null,
                [Validators.required, Validators.min(0.01)]
              ],
              mass_unit: [ans.mass_unit || '', Validators.required],
              rep_year: [ans.rep_year || '', Validators.required],
              region: [ans.region || '', Validators.required],
              note: [ans.note || ''],
              eol: this.fb.array([])
            });

            this.productGroups.push(group);

            ans.eol_methods.forEach((eol: any) =>
              this.getFormArray(group, 'eol').push(
                this.fb.group({
                  eol_method: [eol.eol_method || '', Validators.required],
                  waste_type: [eol.waste_type || ''],
                  share_pct: [
                    eol.share_pct || null,
                    [
                      Validators.required,
                      Validators.min(0),
                      Validators.max(100)
                    ]
                  ]
                })
              )
            );
          });
        }
      } catch (e) {
        console.error('Failed to parse localStorage categories:', e);
      }
    }

    this.productGroups.valueChanges.subscribe(() => this.saveToLocalStorage());

    if (this.usage && this.productGroups.length === 0) this.addProductGroup();
  }

  get productGroups(): FormArray {
    return this.form.get('productGroups') as FormArray;
  }

  getFormControl(group: AbstractControl, field: string): FormControl {
    return group.get(field) as FormControl;
  }

  getFormArray(group: AbstractControl, field: string): FormArray {
    return group.get(field) as FormArray;
  }

  toggleUsage(value: boolean): void {
    this.usage = value;
    if (value && this.productGroups.length === 0) this.addProductGroup();
    if (!value) {
      this.productGroups.clear();
      this.saveToLocalStorage();
    }
  }

  addProductGroup(): void {
    const group = this.fb.group({
      product_label: ['', Validators.required],
      mass_sold: [null, [Validators.required, Validators.min(0.01)]],
      mass_unit: ['', Validators.required],
      rep_year: ['', Validators.required],
      region: ['', Validators.required],
      note: [''],
      eol: this.fb.array([])
    });
    this.productGroups.push(group);
    this.addEolRow(this.getFormArray(group, 'eol'));
  }

  removeProductGroup(index: number): void {
    this.productGroups.removeAt(index);
  }

  addEolRow(array: FormArray): void {
    array.push(
      this.fb.group({
        eol_method: ['', Validators.required],
        waste_type: [''],
        share_pct: [
          null,
          [Validators.required, Validators.min(0), Validators.max(100)]
        ]
      })
    );
  }

  removeEolRow(array: FormArray, index: number): void {
    array.removeAt(index);
  }

  getShareSum(array: FormArray): number {
    return array.controls.reduce((sum, ctrl) => {
      const val = ctrl.get('share_pct')?.value;
      return sum + (parseFloat(val) || 0);
    }, 0);
  }

  saveToLocalStorage(): void {
    const answers = this.productGroups.controls.map(group => {
      const eolArray = this.getFormArray(group, 'eol');
      return {
        product_label: group.get('product_label')?.value,
        mass_sold: group.get('mass_sold')?.value,
        mass_unit: group.get('mass_unit')?.value,
        rep_year: group.get('rep_year')?.value,
        region: group.get('region')?.value,
        note: group.get('note')?.value,
        eol_methods: eolArray.controls.map(ctrl => {
          const wasteTypeValue = ctrl.get('waste_type')?.value;
          let wasteTypeEnglish = '';
          let wasteTypeItalian = '';

          for (const method of this.WASTE_TREATMENT_METHODS) {
            const found = method.subcategories.find(
              (sub: any) => sub.id === wasteTypeValue
            );
            if (found) {
              wasteTypeEnglish = found.label;
              wasteTypeItalian = found.itaLabel;
              break;
            }
          }

          return {
            eol_method: ctrl.get('eol_method')?.value,
            waste_type: wasteTypeValue,
            waste_type_label_en: wasteTypeEnglish,
            waste_type_label_it: wasteTypeItalian,
            share_pct: ctrl.get('share_pct')?.value
          };
        })
      };
    });

    let categories = (this.categoryService.categories() as any[]) || [];
    while (categories.length < 5) categories.push(this.createDefaultCategory());
    if (!categories[4].questions)
      categories[4].questions = [this.createDefaultQuestion()];
    if (categories[4].questions.length === 0)
      categories[4].questions.push(this.createDefaultQuestion());

    categories[4].questions[2].answers = this.usage ? answers : null;
    this.categoryService.persistCategories(categories);
  }

  private createDefaultCategory() {
    return {
      id: '',
      done: false,
      title: '',
      heading: '',
      doubt: '',
      emissions: [],
      questions: [this.createDefaultQuestion()]
    };
  }

  private createDefaultQuestion() {
    return {
      id: '',
      done: false,
      title: '',
      heading: '',
      doubt: '',
      emissions: [],
      answers: null
    };
  }
  getTreatmentMethod() {
    return this.WASTE_TREATMENT_METHODS.map(method => ({
      value: method.name,
      label: this.language === 'english' ? method.name : method.itaName
    }));
  }

  getSubcategories(mainCategory: string | string[]) {
    if (!mainCategory) return [];
    const selectedCategory = this.WASTE_TREATMENT_METHODS.find(
      category => category.name === mainCategory
    )?.subcategories;
    const formattedSubcategories =
      selectedCategory?.map((subcategory: any) => ({
        value: subcategory.id,
        label:
          this.language === 'english' ? subcategory.label : subcategory.itaLabel
      })) || [];
    return formattedSubcategories;
  }

  @HostListener('document:click', ['$event'])
  handleClickOutside(event: MouseEvent) {}
}
